import { gql } from "graphql-request";

// Create a new batch with pre-signed upload URLs
export const CREATE_CUSTOMS_BATCH = gql`
  mutation CreateCustomsBatch($input: CreateCustomsBatchInput!) {
    createCustomsBatch(input: $input) {
      batchId
      documents {
        docId
        filename
        uploadUrl
        s3Key
      }
    }
  }
`;

// Manually trigger processing for all UPLOADED documents in a batch
export const PROCESS_CUSTOMS_BATCH = gql`
  mutation ProcessCustomsBatch($batchId: ID!, $tenantId: ID) {
    processCustomsBatch(batchId: $batchId, tenantId: $tenantId) {
      processed
      succeeded
      failed
    }
  }
`;

// Get batch status with all documents
export const GET_CUSTOMS_BATCH = gql`
  query CustomsBatch($batchId: ID!) {
    customsBatch(batchId: $batchId) {
      batch {
        _id
        tenantId
        clientId
        createdBy
        totalFiles
        statusCounts {
          uploaded
          textExtracted
          keysExtracted
          ready
          failed
        }
        createdAt
        completedAt
      }
      documents {
        docId
        filename
        status
        error {
          code
          message
          stage
        }
      }
    }
  }
`;

// List batches for tenant
export const GET_CUSTOMS_BATCHES = gql`
  query CustomsBatches($tenantId: ID, $clientId: ID, $pageNo: Int, $pageSize: Int) {
    customsBatches(tenantId: $tenantId, clientId: $clientId, pageNo: $pageNo, pageSize: $pageSize) {
      data {
        _id
        tenantId
        clientId
        createdBy
        totalFiles
        statusCounts {
          uploaded
          textExtracted
          keysExtracted
          ready
          failed
        }
        createdAt
        completedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// List documents with filters
export const GET_CUSTOMS_DOCUMENTS = gql`
  query CustomsDocuments(
    $tenantId: ID
    $clientId: ID
    $batchId: ID
    $status: CustomsDocumentStatus
    $clusterId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    customsDocuments(
      tenantId: $tenantId
      clientId: $clientId
      batchId: $batchId
      status: $status
      clusterId: $clusterId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Get single document with download URL
export const GET_CUSTOMS_DOCUMENT = gql`
  query CustomsDocument($docId: ID!, $tenantId: ID) {
    customsDocument(docId: $docId, tenantId: $tenantId) {
      document {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        extractedText
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      downloadUrl
    }
  }
`;

// Search documents by identifier or text
export const SEARCH_CUSTOMS_DOCUMENTS = gql`
  query SearchCustomsDocuments(
    $query: String!
    $tenantId: ID
    $clientId: ID
    $documentType: CustomsDocumentType
    $pageNo: Int
    $pageSize: Int
  ) {
    searchCustomsDocuments(
      query: $query
      tenantId: $tenantId
      clientId: $clientId
      documentType: $documentType
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Get cluster with all documents
export const GET_CUSTOMS_CLUSTER = gql`
  query CustomsCluster($clusterId: ID!) {
    customsCluster(clusterId: $clusterId) {
      cluster {
        _id
        tenantId
        clientId
        clusterKeys {
          type
          value
          sourceDocIds
        }
        entryNumbers
        entrySummaryDocIds
        documentIds
        documentCount
        status
        evidence
        createdAt
        updatedAt
      }
      documents {
        _id
        filename
        status
        documentType
        identifiers {
          type
          value
        }
      }
    }
  }
`;

// List clusters with pagination and optional filters
// Use 'identifier' to search by any identifier type (BL, Container, PO, Invoice, Entry)
export const GET_CUSTOMS_CLUSTERS = gql`
  query CustomsClusters(
    $tenantId: ID
    $clientId: ID
    $entryNumber: String
    $identifier: String
    $pageNo: Int
    $pageSize: Int
  ) {
    customsClusters(
      tenantId: $tenantId
      clientId: $clientId
      entryNumber: $entryNumber
      identifier: $identifier
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        clusterKeys {
          type
          value
          sourceDocIds
        }
        entryNumbers
        entrySummaryDocIds
        documentIds
        documentCount
        status
        evidence
        createdAt
        updatedAt
        latestAuditId
        latestAuditStatus
        keyInsight
        keyInsightCategory
        hasValueDiscrepancies
        hasClassificationDiscrepancies
        auditQueueStatus
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Queue a cluster audit for background processing
export const QUEUE_CLUSTER_AUDIT = gql`
  mutation QueueClusterAudit($clusterId: ID!, $tenantId: ID, $priority: Int) {
    queueClusterAudit(clusterId: $clusterId, tenantId: $tenantId, priority: $priority) {
      queueItem {
        _id
        clusterId
        status
        position
        createdAt
      }
      alreadyQueued
    }
  }
`;

// Queue multiple cluster audits (e.g., "Audit All")
export const QUEUE_MULTIPLE_AUDITS = gql`
  mutation QueueMultipleAudits($clusterIds: [ID!]!, $tenantId: ID) {
    queueMultipleAudits(clusterIds: $clusterIds, tenantId: $tenantId) {
      queued
      alreadyQueued
    }
  }
`;

// Get audit queue statistics
export const GET_AUDIT_QUEUE_STATS = gql`
  query AuditQueueStats($tenantId: ID) {
    auditQueueStats(tenantId: $tenantId) {
      queued
      processing
      completedToday
      failedToday
    }
  }
`;

// Delete a customs document
export const DELETE_CUSTOMS_DOCUMENT = gql`
  mutation DeleteCustomsDocument($docId: ID!, $tenantId: ID) {
    deleteCustomsDocument(docId: $docId, tenantId: $tenantId)
  }
`;

// Delete a customs cluster and all its documents
export const DELETE_CUSTOMS_CLUSTER = gql`
  mutation DeleteCustomsCluster($clusterId: ID!, $tenantId: ID) {
    deleteCustomsCluster(clusterId: $clusterId, tenantId: $tenantId) {
      success
      deletedDocuments
    }
  }
`;

// Get the most recent audit for a cluster
export const GET_CLUSTER_AUDIT = gql`
  query ClusterAudit($clusterId: ID!, $tenantId: ID) {
    clusterAudit(clusterId: $clusterId, tenantId: $tenantId) {
      _id
      clusterId
      tenantId
      clientId
      entryNumber
      status
      confidenceScore
      dataCompleteness
      discrepancies {
        id
        severity
        category
        title
        description
        citations {
          documentId
          documentName
          documentType
          excerpt
          pageNumber
          tradeDataLine
          tradeDataField
        }
        recommendation
        structuredData {
          valueDiscrepancy {
            declaredValue
            declaredSource
            actualValue
            actualSource
            tradeDataValue
            discrepancyAmount
            discrepancyType
          }
          classificationDiscrepancy {
            filedHtsCode
            correctHtsCode
            filedDutyRate
            correctDutyRate
            declaredValue
            dutyDifference
            dutyImpactType
            lineNumber
            entryLine
          }
        }
      }
      analysisText
      thinkingTrail
      documentsAnalyzed
      tradeDataLinesAnalyzed
      modelUsed
      processingTimeMs
      tokenUsage {
        input
        output
      }
      createdAt
      updatedAt
    }
  }
`;

// List audits with filters
export const GET_CLUSTER_AUDITS = gql`
  query ClusterAudits(
    $tenantId: ID
    $clientId: ID
    $status: ClusterAuditStatus
    $pageNo: Int
    $pageSize: Int
  ) {
    clusterAudits(
      tenantId: $tenantId
      clientId: $clientId
      status: $status
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        clusterId
        tenantId
        clientId
        entryNumber
        status
        confidenceScore
        dataCompleteness
        discrepancies {
          id
          severity
          category
          title
        }
        documentsAnalyzed
        tradeDataLinesAnalyzed
        processingTimeMs
        createdAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Aggregate all VALUE_MISMATCH discrepancies
export const GET_VALUE_DISCREPANCIES = gql`
  query ValueDiscrepancies(
    $tenantId: ID
    $clientId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    valueDiscrepancies(
      tenantId: $tenantId
      clientId: $clientId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        auditId
        clusterId
        entryNumber
        auditStatus
        auditDate
        auditUpdatedAt
        confidenceScore
        severity
        title
        description
        recommendation
        declaredValue
        declaredSource
        actualValue
        actualSource
        tradeDataValue
        discrepancyAmount
        discrepancyType
        estimatedDutyImpact
        lineNumber
        entryLine
        lineNumbers
        entryLines
        isMetalSplit
        entryDate
        countryOfOriginCode
        htsNumberFull
        metalPercentage
        paidDuties {
          mfnDuty
          duty201
          duty232
          duty301
          oth99Duties
          totalDuty
        }
        entryLineData {
          lineNumber
          entryLine
          htsCode
          manufacturerId
          countryOfOrigin
          goodsValue
          lineDutyAmount
          chapter99Codes
          hasSec232
        }
        perLineAdjustments {
          lineNumber
          entryLine
          declaredValue
          actualValue
          adjustmentAmount
        }
        discrepancyId
        reviewStatus
        reviewStatusSetBy
        reviewStatusSetAt
        reviewComments {
          id
          text
          authorId
          authorName
          createdAt
        }
      }
      totals {
        totalDiscrepancyAmount
        totalEstimatedDutyImpact
        totalEstimatedDutiesOwed
        totalEstimatedDutiesSaved
        count
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Aggregate all DUTY_CALCULATION discrepancies
export const GET_DUTY_DISCREPANCIES = gql`
  query DutyDiscrepancies(
    $tenantId: ID
    $clientId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    dutyDiscrepancies(
      tenantId: $tenantId
      clientId: $clientId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        auditId
        clusterId
        entryNumber
        auditStatus
        auditDate
        auditUpdatedAt
        confidenceScore
        severity
        title
        description
        recommendation
        citations {
          documentId
          documentName
          documentType
          excerpt
          tradeDataLine
          tradeDataField
        }
        lineNumbers
        entryLines
        isMetalSplit
        discrepancyId
        reviewStatus
        reviewStatusSetBy
        reviewStatusSetAt
        reviewComments {
          id
          text
          authorId
          authorName
          createdAt
        }
      }
      totals {
        count
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Get aggregated HTS classification discrepancies
export const GET_CLASSIFICATION_DISCREPANCIES = gql`
  query ClassificationDiscrepancies(
    $tenantId: ID
    $clientId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    classificationDiscrepancies(
      tenantId: $tenantId
      clientId: $clientId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        auditId
        clusterId
        entryNumber
        auditStatus
        auditDate
        auditUpdatedAt
        confidenceScore
        severity
        title
        description
        recommendation
        citations {
          documentId
          documentName
          documentType
          excerpt
          tradeDataLine
          tradeDataField
        }
        filedHtsCode
        correctHtsCode
        declaredValue
        dutyDifference
        dutyImpactType
        lineNumber
        entryLine
        lineNumbers
        entryLines
        isMetalSplit
        classificationSubType
        filedMetalPercentage
        correctMetalPercentage
        metalPercentageSource
        filedCh99Codes
        correctCh99Codes
        stackingIssueType
        entryDate
        countryOfOriginCode
        metalPercentage
        paidDuties {
          mfnDuty
          duty201
          duty232
          duty301
          oth99Duties
          totalDuty
        }
        entryLineData {
          lineNumber
          entryLine
          htsCode
          manufacturerId
          countryOfOrigin
          goodsValue
          lineDutyAmount
          chapter99Codes
          hasSec232
        }
        discrepancyId
        reviewStatus
        reviewStatusSetBy
        reviewStatusSetAt
        reviewComments {
          id
          text
          authorId
          authorName
          createdAt
        }
      }
      totals {
        totalDutyImpact
        dutiesOwed
        dutiesSaved
        count
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Update review status on a discrepancy
export const UPDATE_DISCREPANCY_REVIEW_STATUS = gql`
  mutation UpdateDiscrepancyReviewStatus(
    $auditId: ID!
    $discrepancyId: String!
    $status: DiscrepancyReviewStatus!
    $tenantId: ID
  ) {
    updateDiscrepancyReviewStatus(
      auditId: $auditId
      discrepancyId: $discrepancyId
      status: $status
      tenantId: $tenantId
    ) {
      id
      reviewStatus
      reviewStatusSetBy
      reviewStatusSetAt
      reviewComments {
        id
        text
        authorId
        authorName
        createdAt
      }
    }
  }
`;

// Add a comment to a discrepancy
export const ADD_DISCREPANCY_COMMENT = gql`
  mutation AddDiscrepancyComment(
    $auditId: ID!
    $discrepancyId: String!
    $text: String!
    $tenantId: ID
  ) {
    addDiscrepancyComment(
      auditId: $auditId
      discrepancyId: $discrepancyId
      text: $text
      tenantId: $tenantId
    ) {
      id
      reviewStatus
      reviewStatusSetBy
      reviewStatusSetAt
      reviewComments {
        id
        text
        authorId
        authorName
        createdAt
      }
    }
  }
`;

// Remove a comment from a discrepancy
export const REMOVE_DISCREPANCY_COMMENT = gql`
  mutation RemoveDiscrepancyComment(
    $auditId: ID!
    $discrepancyId: String!
    $commentId: String!
    $tenantId: ID
  ) {
    removeDiscrepancyComment(
      auditId: $auditId
      discrepancyId: $discrepancyId
      commentId: $commentId
      tenantId: $tenantId
    ) {
      id
      reviewStatus
      reviewStatusSetBy
      reviewStatusSetAt
      reviewComments {
        id
        text
        authorId
        authorName
        createdAt
      }
    }
  }
`;

// Re-extract structured data from a discrepancy's description
export const RE_EXTRACT_DISCREPANCY_DATA = gql`
  mutation ReExtractDiscrepancyData(
    $auditId: ID!
    $discrepancyId: String!
    $tenantId: ID
  ) {
    reExtractDiscrepancyData(
      auditId: $auditId
      discrepancyId: $discrepancyId
      tenantId: $tenantId
    ) {
      success
      declaredValue
      actualValue
      discrepancyAmount
      discrepancyType
      error
    }
  }
`;

// Run AI audit on a cluster
export const RUN_CLUSTER_AUDIT = gql`
  mutation RunClusterAudit($clusterId: ID!, $tenantId: ID) {
    runClusterAudit(clusterId: $clusterId, tenantId: $tenantId) {
      _id
      clusterId
      tenantId
      clientId
      entryNumber
      status
      confidenceScore
      dataCompleteness
      discrepancies {
        id
        severity
        category
        title
        description
        citations {
          documentId
          documentName
          documentType
          excerpt
          pageNumber
          tradeDataLine
          tradeDataField
        }
        recommendation
        structuredData {
          valueDiscrepancy {
            declaredValue
            declaredSource
            actualValue
            actualSource
            tradeDataValue
            discrepancyAmount
            discrepancyType
          }
          classificationDiscrepancy {
            filedHtsCode
            correctHtsCode
            filedDutyRate
            correctDutyRate
            declaredValue
            dutyDifference
            dutyImpactType
            lineNumber
            entryLine
          }
        }
      }
      analysisText
      thinkingTrail
      documentsAnalyzed
      tradeDataLinesAnalyzed
      modelUsed
      processingTimeMs
      tokenUsage {
        input
        output
      }
      createdAt
      updatedAt
    }
  }
`;

// Manually update discrepancy values
export const UPDATE_DISCREPANCY_VALUES = gql`
  mutation UpdateDiscrepancyValues(
    $auditId: ID!
    $discrepancyId: String!
    $declaredValue: Float!
    $actualValue: Float!
    $lineNumbers: [Int]
    $tenantId: ID
  ) {
    updateDiscrepancyValues(
      auditId: $auditId
      discrepancyId: $discrepancyId
      declaredValue: $declaredValue
      actualValue: $actualValue
      lineNumbers: $lineNumbers
      tenantId: $tenantId
    ) {
      success
      error
    }
  }
`;

// Manually update classification discrepancy values
export const UPDATE_CLASSIFICATION_DISCREPANCY_VALUES = gql`
  mutation UpdateClassificationDiscrepancyValues(
    $auditId: ID!
    $discrepancyId: String!
    $filedHtsCode: String!
    $correctHtsCode: String!
    $filedDutyRate: Float!
    $correctDutyRate: Float!
    $lineNumber: Int
    $tenantId: ID
  ) {
    updateClassificationDiscrepancyValues(
      auditId: $auditId
      discrepancyId: $discrepancyId
      filedHtsCode: $filedHtsCode
      correctHtsCode: $correctHtsCode
      filedDutyRate: $filedDutyRate
      correctDutyRate: $correctDutyRate
      lineNumber: $lineNumber
      tenantId: $tenantId
    ) {
      success
      error
    }
  }
`;

// Get entry-document linking stats (accurate across all data)
export const GET_ENTRY_LINKING_STATS = gql`
  query EntryLinkingStats($tenantId: ID, $clientId: ID) {
    entryLinkingStats(tenantId: $tenantId, clientId: $clientId) {
      totalEntries
      linkedEntries
      auditResults {
        approved
        needsReview
        discrepancies
        notAudited
      }
      missingProducts
      totalValue
      totalDuty
      timeSeries {
        month
        importValue
        duties
      }
    }
  }
`;

// Add an identifier to a document
export const ADD_DOCUMENT_IDENTIFIER = gql`
  mutation AddDocumentIdentifier($docId: ID!, $type: CustomsIdentifierType!, $value: String!, $tenantId: ID) {
    addDocumentIdentifier(docId: $docId, type: $type, value: $value, tenantId: $tenantId) {
      _id
      identifiers {
        type
        value
        confidence
        extractedBy
      }
      clusterId
    }
  }
`;

// Update an identifier on a document
export const UPDATE_DOCUMENT_IDENTIFIER = gql`
  mutation UpdateDocumentIdentifier($docId: ID!, $index: Int!, $type: CustomsIdentifierType!, $value: String!, $tenantId: ID) {
    updateDocumentIdentifier(docId: $docId, index: $index, type: $type, value: $value, tenantId: $tenantId) {
      _id
      identifiers {
        type
        value
        confidence
        extractedBy
      }
      clusterId
    }
  }
`;

// Remove an identifier from a document
export const REMOVE_DOCUMENT_IDENTIFIER = gql`
  mutation RemoveDocumentIdentifier($docId: ID!, $index: Int!, $tenantId: ID) {
    removeDocumentIdentifier(docId: $docId, index: $index, tenantId: $tenantId) {
      _id
      identifiers {
        type
        value
        confidence
        extractedBy
      }
      clusterId
    }
  }
`;

// Delete a discrepancy from an audit
export const DELETE_DISCREPANCY = gql`
  mutation DeleteDiscrepancy(
    $auditId: ID!
    $discrepancyId: String!
    $tenantId: ID
  ) {
    deleteDiscrepancy(
      auditId: $auditId
      discrepancyId: $discrepancyId
      tenantId: $tenantId
    ) {
      success
      error
    }
  }
`;
